import {
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
} from "react";
import { WalletTransactionsHeaderTabs } from "./wallet-transactions-header.admin.partials";
import useWalletTranasactionsStore from "../../../store/wallet-transactions.store";
import { useGetPayoutTransactionsAdminHttp } from "../../../hooks/http/admin/payouts/get-payout-transcations.admin.http";
import {
  type IPayoutTransactionMinimal,
  IPayoutType,
  ITransationStatus,
} from "../../../types/models/payout-transaction.modals.types";
import { DateTime } from "luxon";
import { handleHttpError } from "../../../utils/utils";
import { toast } from "../../../hooks/use-toast";
import { useApproveInvoiceDetailsTransactionAdminHttp } from "../../../hooks/http/admin/payouts/approve-invoice-details-transaction.admin.http";
import { useReleaseGstTransactionAdminHttp } from "../../../hooks/http/admin/payouts/release-gst-transaction.admin.http";
import { useRejectInvoiceDetailsTransactionAdminHttp } from "../../../hooks/http/admin/payouts/reject-invoice-details-transaction.admin.http";
import {
  type ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DataTableComponents } from "../../../components/tables/table.components";
import type { IPagination } from "../../../types/models/pagination.models.types";
import Spinner from "../../../components/spinner.components";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";

interface IWalletTransactionsListAdminPartialProps {
  tab: WalletTransactionsHeaderTabs;
}

export const WalletTransactionsListAdminPartial: FunctionComponent<
  IWalletTransactionsListAdminPartialProps
> = ({ tab }) => {
  const { transactions, setTransactions, removeTransactionById } =
    useWalletTranasactionsStore();
  const [pageNo, setPageNo] = useState(1);
  const [paginationData, setPaginationData] = useState<IPagination>();
  const navigate = useNavigate();

  const {
    execute: getPayoutRequests,
    fetching: fetchingPayouts,
    cancel: cancelGetPayoutRequests,
  } = useGetPayoutTransactionsAdminHttp();
  const {
    execute: approveInvoiceDetailsTransaction,
    fetching: approvingInvoiceDetailsTransaction,
  } = useApproveInvoiceDetailsTransactionAdminHttp();
  const {
    execute: releaseGstTransactionTransaction,
    fetching: releasingGstTransactionTransaction,
  } = useReleaseGstTransactionAdminHttp();
  const {
    execute: rejectInvoiceDetailsTransaction,
    fetching: rejectingInvoiceDetailsTransaction,
  } = useRejectInvoiceDetailsTransactionAdminHttp();

  async function getTransactions(
    existingTransactions: IPayoutTransactionMinimal[],
  ) {
    cancelGetPayoutRequests();
    let transactionStatus: ITransationStatus | null = null;
    if (tab === WalletTransactionsHeaderTabs.INVOICE_APPROVAL) {
      transactionStatus = ITransationStatus.INVOICE_DETAILS_PENDING_APPROVAL;
    } else if (tab === WalletTransactionsHeaderTabs.GST_APPROVAL) {
      transactionStatus = ITransationStatus.GST_PENDING;
    }

    const queryParams: Record<string, string | number | boolean> = {
      page: pageNo,
      size: 50,
    };
    if (transactionStatus) {
      queryParams.transactionStatus = transactionStatus;
    }

    const response = await getPayoutRequests({
      body: null,
      queryParams,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }

    const newTransactions = response.data.transactions.filter((transaction) => {
      return (
        existingTransactions.findIndex((i) => i.id === transaction.id) === -1
      );
    });

    setTransactions([...existingTransactions, ...newTransactions]);
    setPaginationData(response.data.pagination);
  }

  async function approveInvoice(transaction: IPayoutTransactionMinimal) {
    const response = await approveInvoiceDetailsTransaction({
      pathParams: { id: transaction.id },
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }

    removeTransactionById(transaction.id);
  }

  async function rejectInvoice(transaction: IPayoutTransactionMinimal) {
    const response = await rejectInvoiceDetailsTransaction({
      pathParams: { id: transaction.id },
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }

    removeTransactionById(transaction.id);
  }

  async function releaseGst(transaction: IPayoutTransactionMinimal) {
    const response = await releaseGstTransactionTransaction({
      pathParams: { id: transaction.id },
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }

    removeTransactionById(transaction.id);
  }

  useEffect(() => {
    setTransactions([]);
    setPageNo(1);
    getTransactions([]);
  }, [tab]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (pageNo > 1) {
      getTransactions(transactions || []);
    }
  }, [pageNo]);

  const approveButtonDisabled =
    approvingInvoiceDetailsTransaction || releasingGstTransactionTransaction;
  const rejectButtonDisabled = rejectingInvoiceDetailsTransaction;

  const columns = useMemo<ColumnDef<IPayoutTransactionMinimal>[]>(() => {
    const cols: ColumnDef<IPayoutTransactionMinimal>[] = [
      {
        accessorKey: "creator",
        header: "Creator",
        cell: ({ row }) => {
          const creator = row.original.creator;
          return (
            <div>
              <p className="font-semibold tracking-wider">{creator.name}</p>
              <p className="text-secondary tracking-wider">
                {creator.phoneNumber}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "transactionStatus.timestamp",
        header: "Date",
        cell: ({ row }) => {
          return (
            <p className="font-semibold tracking-wider">
              {DateTime.fromISO(
                row.original.transactionStatus.timestamp.toString(),
              ).toLocaleString()}
            </p>
          );
        },
      },
      {
        accessorKey: "transactionStatus.status",
        header: "Status",
        cell: ({ row }) => (
          <p className="font-semibold tracking-wider">
            {row.original.transactionStatus.status}
          </p>
        ),
      },
    ];

    if (
      tab === WalletTransactionsHeaderTabs.INVOICE_APPROVAL ||
      tab === WalletTransactionsHeaderTabs.GST_APPROVAL
    ) {
      cols.push({
        accessorKey: "invoiceDetails",
        header: "Invoice Details",
        cell: ({ row }) => {
          const details = row.original.invoiceDetails;
          return (
            <div>
              <p className="font-semibold tracking-wider">
                ID: {details?.invoiceNumber}
              </p>
              <p className="text-secondary tracking-wider">
                Date:{" "}
                {details
                  ? DateTime.fromISO(
                      details.dateOfInvoice.toString(),
                    ).toLocaleString()
                  : ""}
              </p>
            </div>
          );
        },
      });
    }

    cols.push({
      accessorKey: "totalAmount",
      header: "Amount",
      cell: ({ row }) => {
        const transaction = row.original;
        const isCredit = transaction.transactionType === IPayoutType.CREDITED;
        return (
          <p
            className={`font-semibold tracking-wider ${
              isCredit ? "text-green-500" : "text-red-500"
            }`}
          >
            {isCredit ? "+" : "-"}
            {transaction.totalAmount}
          </p>
        );
      },
    });

    cols.push({
      accessorKey: "id",
      header: "",
      cell: ({ row }) => {
        const transaction = row.original;
        return (
          <div className="flex justify-end items-center gap-x-4">
            <Button
              onClick={() => {
                navigate(transaction.id);
              }}
              className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
            >
              View Details
            </Button>
            <button
              type="button"
              className={`px-4 py-2 border rounded-md text-approvebuttontext tracking-wider ${
                approveButtonDisabled
                  ? "border-approvebuttonborder/20 bg-neutral-500/80 cursor-not-allowed"
                  : "border-approvebuttonborder bg-approvebuttonbg"
              }`}
              onClick={async () => {
                if (approveButtonDisabled) {
                  return;
                }
                if (tab === WalletTransactionsHeaderTabs.INVOICE_APPROVAL) {
                  await approveInvoice(transaction);
                } else if (tab === WalletTransactionsHeaderTabs.GST_APPROVAL) {
                  await releaseGst(transaction);
                }
              }}
              disabled={approveButtonDisabled}
            >
              <p className="font-bold text-sm">Approve</p>
            </button>
            {tab === WalletTransactionsHeaderTabs.INVOICE_APPROVAL && (
              <button
                type="button"
                className={`px-4 py-2 border text-nowrap rounded-md font-bold text-sm text-rejectbuttontext border-rejectbuttonborder tracking-wider ${
                  rejectButtonDisabled
                    ? "opacity-50 cursor-not-allowed bg-rejectbuttonbg/20"
                    : ""
                }`}
                onClick={async () => {
                  if (rejectButtonDisabled) {
                    return;
                  }
                  await rejectInvoice(transaction);
                }}
                disabled={rejectButtonDisabled}
              >
                Reject
              </button>
            )}
          </div>
        );
      },
    });

    return cols;
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  }, [tab, approveButtonDisabled, rejectButtonDisabled]);

  const table = useReactTable({
    data: transactions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
  });

  if (!transactions || (transactions.length <= 0 && fetchingPayouts)) {
    return (
      <div className="flex justify-center items-center pt-20 w-full">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-auto -z-10 pt-4">
      <DataTableComponents
        table={table as ReturnType<typeof useReactTable>}
        columns={columns as ColumnDef<IPayoutTransactionMinimal>[]}
        setPageNo={setPageNo}
        paginationData={paginationData}
        loading={fetchingPayouts}
        tableType="payout"
      />
    </div>
  );
};
